import { Link } from 'react-router-dom';
import './auth.scss';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import clsx from 'clsx';
import { yupPasswordValidator } from '@myinnsworth/helpers/passwordHelpers';
import { authenticationHttpService } from '@myinnsworth/httpServices';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';

const registerSchema = yup.object({
    email: yup.string().email('Invalid Email Address').required('Invalid Email Address'),
    password: yupPasswordValidator,
    forename: yup.string().required('You must enter your forename'),
    surname: yup.string().required('You must enter your surname'),
    confirmPassword: yup.string()
        .required('You must confirm your password')
        .oneOf([yup.ref('password')], 'Passwords do not match')
}).required();

interface IRegisterSchema {
    email: string;
    password: string;
    forename: string;
    surname: string;
    confirmPassword: string;
}

enum RegistrationState {
    None,
    Success
}

export default function SignUp() {
    const [state, setState] = useState<RegistrationState>(RegistrationState.None);
    const { register: registerInput, handleSubmit, clearErrors, setError, formState: { errors } } = useForm<IRegisterSchema>({
        resolver: yupResolver(registerSchema)
    });

    const onSubmit = async (formData: IRegisterSchema) => {
        clearErrors();
        try {
            await authenticationHttpService.registerContact(formData);
            setState(RegistrationState.Success);
        } catch(ex) {
            if(axios.isAxiosError(ex)) {
                const error = ex as AxiosError;
                switch(error.response!.status) {
                    case 404: { //404 - Contact not found
                        setError('email', { message: 'Email address not found'});
                        break;
                    }
                    case 406: { //406 - Passwords do not match
                        setError('confirmPassword', { message: 'Passwords do not match'});
                        break;
                    }
                    case 409: { //409 - Contact already registered
                        setError('email', { message: 'This email address is already registered'});
                        break;
                    }
                    case 417: { //417 - Password complexity
                        setError('password', { message: 'Password does not meet complexity rules'});
                        break;
                    }
                }
            }
        }
    };

    return (
        <>
            <h6>Register for My.Innsworth</h6>
            <div className="spacing-l"></div>

            {state === RegistrationState.None && (
                <form className="auth-form-container"  onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className={clsx('form-control', 'normal', { 'error': errors.forename })}>
                        <input  {...registerInput('forename')}
                                type="text" 
                                placeholder='Forename'></input>
                        {errors.forename && (<p>{errors.forename.message}</p>)}
                        {!errors.forename && (<p>What is your first name?</p>)}
                    </div>

                    <div className="spacing-m"></div>

                    <div className={clsx('form-control', 'normal', { 'error': errors.surname })}>
                        <input  {...registerInput('surname')} 
                                type="text" 
                                placeholder='Surname'></input>
                        {errors.surname && (<p>{errors.surname.message}</p>)}
                        {!errors.surname && (<p>What is your last name?</p>)}
                    </div>

                    <div className="spacing-m"></div>

                    <div className={clsx('form-control', 'normal', { 'error': errors.email })}>
                        <input  {...registerInput('email')} 
                                type="text" 
                                placeholder='Email Address'></input>
                        {errors.email && (<p>{errors.email.message}</p>)}
                        {!errors.email && (<p>Please provide an email address</p>)}
                    </div>

                    <div className="spacing-m"></div>

                    <div className={clsx('form-control', 'normal', { 'error': errors.password })}>
                        <input  {...registerInput('password')} 
                                type="password" 
                                placeholder='Password'></input>
                        {errors.password && (<p>{errors.password.message}</p>)}
                        {!errors.password && (<p>Please enter a password</p>)}
                    </div>

                    <div className="spacing-m"></div>

                    <div className={clsx('form-control', 'normal', { 'error': errors.confirmPassword })}>
                        <input  {...registerInput('confirmPassword')} 
                                type="password" 
                                placeholder='Confirm password'></input>
                        {errors.confirmPassword && (<p>{errors.confirmPassword.message}</p>)}
                        {!errors.confirmPassword && (<p>Please enter a password</p>)}
                    </div>

                    <div className="spacing-m"></div>

                    <div className="flex justify-center">
                        <button type="submit" className="btn primary medium">Register for my.innsworth</button>
                    </div>

                    <div className="spacing-m"></div>

                    <div className="flex direction-column align-center">
                        <p className='light'>Already have an account?</p>
                        <div className="spacing-xxs"></div>
                        <Link className='link secondary' to={"/auth/sign-in"} >Login</Link>
                    </div>

                </form>
            )}

            {state === RegistrationState.Success && (<>
                <h6 className='text-center'>Registration Successful!  A registration email has been sent to your address, please click the link in the mail to confirm registration</h6>
                <div className="spacing-l"></div>

                <div className="flex direction-column align-center">
                    <Link className='btn primary large back' to={"/auth/sign-in"} >Back to Login</Link>
                </div>
            </>)}
        </>
    )
}